import React, { useCallback, useEffect, useRef } from 'react';
import styled from 'astroturf/react';
import throttle from 'lodash/throttle';

import ImpressionWatcher from 'js/utils/impressions/ImpressionWatcher';

import { Page } from './Page';
import { BuildVuPage, getManifest } from './api';
import { useAttachmentShown } from './attachment_shown_store';
import { useScribdEvents } from './scribd_events_tracker';
import { InterstitialRegistry } from './InterstitialRegistry';
import { InterstitialConfig } from './interstitials/types';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';

// Only preload 1 page on mobile - this empirically reduces INP.
// (Probably because mobile devices are generally lower-powered in both network and rendering.)
const isMobile =
  typeof window !== 'undefined' &&
  (window.innerWidth <= 768 || window.matchMedia('(max-width: 767px)').matches);

const PRELOAD_PAGES = isMobile ? 1 : 3;

const Container = styled.div`
  background: #f3f4fa;
`;

const InterstitialWrapper = styled.div`
  width: 100%;
`;

type PageSpec = {
  page: BuildVuPage;
  load: boolean;
};

type RenderItem =
  | { type: 'page'; pageSpec: PageSpec }
  | { type: 'interstitial'; config: InterstitialConfig };

export type AttachmentProps = {
  attachmentId: string;
  firstPageUrl?: string;
  shown?: boolean;
  pages?: number;
  interstitials?: InterstitialConfig[];
  className?: string;
};

const buildRenderItems = (
  pageSpecs: PageSpec[] | null,
  interstitials?: InterstitialConfig[],
  pageLimit?: number,
): RenderItem[] => {
  if (!pageSpecs) return [];

  const limitedPages = pageSpecs.slice(0, pageLimit);
  const items: RenderItem[] = [];

  limitedPages.forEach((pageSpec) => {
    // Add the page
    items.push({ type: 'page', pageSpec });

    // Check if any interstitials should follow this page
    // Multiple interstitials with the same afterPage will be rendered in array order
    const interstitialsForPage =
      interstitials?.filter((i) => i.afterPage === pageSpec.page.page_number) ||
      [];

    // Preserve order from the interstitials array
    interstitialsForPage.forEach((interstitial) => {
      items.push({
        type: 'interstitial',
        config: interstitial,
      });
    });
  });

  return items;
};

export const Attachment: React.FC<AttachmentProps> = ({
  attachmentId,
  firstPageUrl,
  shown: forceShown,
  pages,
  interstitials,
  className,
}) => {
  // Create placeholder page spec for first page - assuming that if Attachment is
  // shown, the PDF exists, and will always have at least one page.
  const initialPageSpecs = React.useMemo(() => {
    if (firstPageUrl) {
      const placeholderPage: BuildVuPage = {
        page_url: firstPageUrl,
        page_number: 1,
        width: 850, // Assuming the common US Letter size 8.5x11"
        height: 1100,
        preload: true,
      };
      return [{ page: placeholderPage, load: true }];
    }
    return null;
  }, [firstPageUrl]);

  const [pageSpecs, setPageSpecs] = React.useState<PageSpec[] | null>(
    initialPageSpecs,
  );
  const [manifestFetched, setManifestFetched] = React.useState(false);

  const visiblePagesRef = useRef<Set<number>>(new Set());
  const currentPageRef = useRef<number>(1);

  const { enqueueEvent } = useScribdEvents(attachmentId);

  const updateCurrentPage = useCallback(() => {
    if (visiblePagesRef.current.size === 0) return;

    const sortedPages = Array.from(visiblePagesRef.current).sort(
      (a, b) => a - b,
    );
    const topmostPage = sortedPages[0];

    if (topmostPage !== currentPageRef.current) {
      currentPageRef.current = topmostPage;

      window.dispatchEvent(
        new CustomEvent('buildVuPageChanged', {
          detail: { pageNumber: topmostPage },
        }),
      );
    }
  }, []);

  // TODO: Decouple useAttachmentShown
  const statefulShown = useAttachmentShown();
  const shown = forceShown ?? statefulShown;

  // Fetch the real manifest and replace placeholder
  useEffect(() => {
    if (manifestFetched) return;

    if (shown) {
      getManifest(attachmentId)
        .then((manifest) => {
          setPageSpecs(
            manifest.map((page) => ({
              page,
              load: page.page_number < PRELOAD_PAGES + 1,
            })),
          );
          setManifestFetched(true);
        })
        .catch(console.error);
    }
  }, [attachmentId, manifestFetched, shown]);

  // Set up throttled scroll listener for smooth page updates
  useEffect(() => {
    const throttledUpdate = throttle(updateCurrentPage, 150);

    window.addEventListener('scroll', throttledUpdate, { passive: true });

    return () => {
      window.removeEventListener('scroll', throttledUpdate);
    };
  }, [updateCurrentPage]);

  const onIntersect = useCallback(
    (pageNumber: number, isIntersecting: boolean) => {
      if (isIntersecting) {
        visiblePagesRef.current.add(pageNumber);
      } else {
        visiblePagesRef.current.delete(pageNumber);
      }

      updateCurrentPage();

      // When a page comes into view, set its load flag to true, as well as the
      // PRELOAD_PAGES number of pages above and below it
      // Only when entering view, othwerwise we'd load the entire thing.
      if (!isIntersecting || !pageSpecs) return;

      let changed = false;
      const newPageSpecs = pageSpecs.map((spec) => {
        if (
          spec.page.page_number >= pageNumber - PRELOAD_PAGES &&
          spec.page.page_number <= pageNumber + PRELOAD_PAGES &&
          !spec.load
        ) {
          changed = true;
          return { ...spec, load: true };
        } else {
          return spec;
        }
      });

      if (changed) {
        setPageSpecs(newPageSpecs);
        enqueueEvent({
          event: 'pageChanged',
          page: pageNumber,
        });
      }
    },
    [enqueueEvent, pageSpecs, updateCurrentPage],
  );

  const renderItems = buildRenderItems(pageSpecs, interstitials, pages);

  return (
    <Container
      id="scribd-attachment-container"
      className={className}
      style={{ display: shown ? 'block' : 'none' }}
    >
      {renderItems.map((item) => {
        if (item.type === 'page') {
          const { page, load } = item.pageSpec;
          return (
            <ImpressionWatcher
              key={page.page_number}
              entity_id={`${attachmentId}${page.page_number}`}
              entity_type={17}
              source="build_vu_attachment_page"
              extra={{ attachment_id: attachmentId, page: page.page_number }}
              stopPropagation={false}
            >
              <Page page={page} load={load} onIntersect={onIntersect} />
            </ImpressionWatcher>
          );
        } else if (item.type === 'interstitial') {
          const { key, componentKey, afterPage } = item.config;

          // Render the appropriate component based on type
          let interstitialElement: React.ReactElement;
          if (componentKey === 'related-papers') {
            const RelatedPapersComponent = InterstitialRegistry[componentKey];
            interstitialElement = (
              <RelatedPapersComponent papers={item.config.papers} />
            );
          } else if (componentKey === 'signup-banner') {
            const SignupBannerComponent = InterstitialRegistry[componentKey];
            interstitialElement = <SignupBannerComponent />;
          } else {
            // Exhaustiveness check
            const exhaustiveCheck: never = componentKey;
            throw new Error(`Unknown interstitial type: ${exhaustiveCheck}`);
          }

          return (
            <ImpressionWatcher
              key={key}
              entity_id={`${attachmentId}_${key}`}
              entity_type={ImpressionEntity.InterstitialBanner}
              source="build_vu_attachment_interstitial"
              extra={{
                attachment_id: attachmentId,
                interstitial_key: key,
                after_page: afterPage,
              }}
              stopPropagation={false}
            >
              <InterstitialWrapper>{interstitialElement}</InterstitialWrapper>
            </ImpressionWatcher>
          );
        } else {
          // Exhaustiveness check - should never reach here
          const exhaustiveCheck: never = item;
          throw new Error(`Unknown render item type: ${exhaustiveCheck}`);
        }
      })}
    </Container>
  );
};
